import { AddMessageBoardParams } from '../../../params/redux/messageboard';
import { createMessageBoardThunk } from '../../../redux/thunks/messageboard';
import CustomInput from '../../reusable/input';
import CustomModal from '../../modals';
import ImageClass from '../../../assets';
import { nameSchema } from '../../../schema';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../../../redux/store';
import { Avatar, Box, Button, Checkbox, Typography } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: AddMessageBoardModal
 * @paragraph: A modal component for adding a new message board. Uses Formik for form handling
 * and integrates with Redux for message board creation.
 * @param: {boolean} open - Whether the modal is open or not.
 * @param: {function} onClose - Function to close the modal.
 */

const AddMessageBoardModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    /**
     * @heading3: Variables
     * @ol:
     * dispatch: {AppDispatch} - The Redux dispatch function to trigger actions.
     * loaders: An object containing the loader details from the Redux store.
     */
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const { loaders } = useSelector((state: RootState) => state.messageboard);
    const { currentUser } = useSelector((state: RootState) => state.app);
    const { customers } = useSelector((state: RootState) => state.customer);
    const initialValues: AddMessageBoardParams = {
        name: '',
        description: '',
        assigned_to: [] as number[],
        client_id: id ? parseInt(id as string, 10) : 0
    };

    /**
     * @heading3: Functions
     * @heading4: Handles the form submission by dispatching a Redux thunk.
     * @param: {PipelineParams} values - The form values for creating a message board.
     */
    const handleSubmit = async (values: AddMessageBoardParams) => {
        try {
            // Dispatch the createMessageBoardThunk with form values
            await dispatch(createMessageBoardThunk(values)).unwrap();
            // Close the modal on successful submission
            onClose();
        } catch (e) {
            // Catch errors
        }
    };

    return <CustomModal open={open} handleClose={loaders.isCreating ? () => null : onClose} title="Create Message Board">
        <Formik
            initialValues={initialValues}
            validationSchema={nameSchema}
            onSubmit={handleSubmit}
        >
            {({ values, handleChange, handleBlur, errors, touched, setFieldValue }) => (
                <Form>
                    <Box display="flex" flexDirection="column" gap={1}>
                        {/* Input field for message board name */}
                        <CustomInput
                            value={values.name}
                            onChange={handleChange('name')}
                            onBlur={handleBlur('name')}
                            error={touched.name && errors.name}
                            fullWidth
                            label="Topic name"
                            placeholder="Type here"
                            variant="outlined"
                            disabled={loaders.isCreating}
                            mandatoryFields={true}
                        />
                        <CustomInput
                            type="textarea"
                            rows={4}
                            value={values.description}
                            onChange={handleChange('description')}
                            onBlur={handleBlur('description')}
                            error={touched.description && errors.description}
                            fullWidth
                            label="Message body"
                            placeholder="Type here"
                            variant="outlined"
                            disabled={loaders.isCreating}
                            mandatoryFields={true}
                        />
                        <Box display="flex" flexDirection="column" gap={1} p={2} maxHeight="250px" minHeight="250px" overflow="auto">
                            {customers
                            .filter((user) => user.id !== currentUser?.id)
                            .map((user) => (
                                <Box key={user.id} display="flex" justifyContent="space-between" alignItems="center" py={1}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Avatar
                                            src={ImageClass.userPlacedholder}
                                        />
                                        <Typography variant="body2">{`${user.first_name} ${user.last_name}`}</Typography>
                                    </Box>
                                    <Box>
                                        <Field
                                            as={Checkbox}
                                            name="selectedUsers"
                                            value={user.id}
                                            checked={values.assigned_to.includes(user.id)}
                                            onChange={() => {
                                                if (values.assigned_to.includes(user.id)) {
                                                    setFieldValue('assigned_to', values.assigned_to.filter((assingee_id) => assingee_id !== user.id));
                                                } else {
                                                    setFieldValue('assigned_to', [...values.assigned_to, user.id]);
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>
                            ))}
                            {touched.assigned_to && errors.assigned_to && (
                                <div style={{ color: 'red', fontSize: '0.9rem' }}>
                                    {errors.assigned_to}
                                </div>
                            )}
                        </Box>
                        {/* Action buttons for form submission or cancellation */}
                        <Box display="flex" gap={2} mt={2}>
                            <Button variant="outlined" color="primary" onClick={onClose} fullWidth disabled={loaders.isCreating}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained" color="primary" fullWidth disabled={loaders.isCreating}>
                                {loaders.isCreating ? 'Creating...' : 'Create'}
                            </Button>
                        </Box>
                    </Box>
                </Form>
            )}
        </Formik>
    </CustomModal>;
};

export default AddMessageBoardModal;